/**
 * Состояние встреч (DEV-1947, экран «Встречи» — DEV-1973).
 *
 * Стор, а не состояние экрана плана: приглашение приходит человеку, где бы он
 * ни был, и баннер о нём висит в общем каркасе. Экран плана и баннер смотрят в
 * одно и то же состояние — иначе «Присоединиться» в шапке и мит-режим внизу
 * расходились бы в том, идёт ли встреча.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { isForbidden as forbiddenResponse } from '@/api/errors'
import { useAuthStore } from '@/features/auth/store'

import {
  attachMeet,
  cancelMeeting,
  endMeeting,
  getMeeting,
  getMeetingState,
  inviteToMeeting,
  inviteToSession,
  joinMeeting,
  listMeetings,
  rescheduleMeeting,
  removeFromMeeting,
  retryMeetingProcessing,
  resendMeetingInvites,
  restoreMeeting,
  retryMeetingRoom,
  scheduleMeeting,
  streamMeeting,
  MeetingStreamClosed,
  type MeetingInvite,
  type MeetingScope,
  type MeetingSession,
  type RescheduleMeetingInput,
  type ScheduleMeetingInput,
} from './api'
import { MeetingPresence, NoteDoc, type PeerState } from './collab'
import { useNotesStore } from './notesStore'

/**
 * Паузы перед повторной подпиской, миллисекунды.
 *
 * Первая короткая — обрыв чаще всего мгновенный (выкат сервиса, моргнувший
 * wifi), и ждать после него пять секунд значит показывать человеку мёртвый
 * экран на ровном месте. Дальше пауза растёт: если сервис лежит, долбить его
 * каждую секунду с каждой вкладки бессмысленно.
 */
const RECONNECT_DELAYS = [1_000, 2_000, 5_000]

export const useMeetingStore = defineStore('meetings', () => {
  const auth = useAuthStore()

  const session = ref<MeetingSession | null>(null)
  const invites = ref<MeetingInvite[]>([])
  const error = ref<unknown>(null)

  /**
   * Состояние встреч закрыто правами: спрашивать больше не о чем.
   *
   * Опрос в баннере — страховка на время, пока лежит поток уведомлений, и от
   * сетевого сбоя она честно лечится повтором. Отказ по правам повтором не
   * лечится никогда: у человека без `tracker:read` он приходил на каждый тик,
   * круглые сутки, из каждой вкладки — 404 отклонённых запроса за три часа
   * (DEV-2136).
   */
  const isForbidden = ref(false)

  /**
   * Человек нажал «Присоединиться» в баннере: плану остаётся открыть мит-режим,
   * когда он до него доедет. Через стор, а не через параметр адреса — режим не
   * состояние страницы, а способ её смотреть.
   */
  const pendingOpen = ref(false)

  /**
   * Связь с встречей потеряна — показываем это, только когда попытка
   * подключиться уже провалилась.
   *
   * Не «поток сейчас не открыт»: между попытками и в первую миллисекунду
   * подключения он тоже не открыт, и красная надпись на ровном месте пугала бы
   * человека там, где всё в порядке.
   */
  const isOffline = ref(false)

  let controller: AbortController | null = null

  const isActive = computed(() => !!session.value)
  const isAuthor = computed(() => session.value?.iAmAuthor ?? false)

  /** Кто на встрече сейчас — позванные, но не пришедшие, в счёт не идут */
  const participants = computed(() =>
    (session.value?.participants ?? []).filter((participant) => participant.joined),
  )

  /** Все, кого касается встреча: сначала пришедшие, следом ещё ожидаемые */
  const everyone = computed(() => [
    ...participants.value,
    ...(session.value?.participants ?? []).filter((participant) => !participant.joined),
  ])

  /**
   * История встреч — левая колонка экрана (DEV-1971).
   *
   * Отдельно от `session`: та отвечает на «где я сейчас», а список — на «где я
   * был». Идущая встреча есть и там и там, и это одна и та же встреча.
   */
  const meetings = ref<MeetingSession[]>([])
  const isListLoading = ref(false)
  const isListLoaded = ref(false)

  const loadMeetings = async () => {
    isListLoading.value = true

    try {
      meetings.value = await listMeetings()
      isListLoaded.value = true
      error.value = null
    } catch (failure) {
      error.value = failure
    } finally {
      isListLoading.value = false
    }
  }

  /**
   * Живые документы открытых заметок и присутствие соседей (DEV-1974).
   *
   * В сторе, а не в компоненте: правки приезжают потоком встречи, который
   * держит он же, и раскладывать их по карточкам больше некому. Сами документы
   * реактивными не делаются — Yjs держит своё состояние сам, а экран будит
   * счётчик правок.
   */
  const docs = new Map<string, NoteDoc>()
  const revision = ref(0)
  const peers = ref<PeerState[]>([])

  /**
   * Счётчик изменений открытой встречи: статус, состав, конец созвона.
   *
   * Экран перечитывает по нему свою карточку. Отдавать саму встречу стор не
   * может — открытых лент столько же, сколько вкладок, а поток он держит один.
   */
  const meetingRevision = ref(0)

  let presence: MeetingPresence | null = null

  const touch = () => {
    revision.value += 1
    peers.value = presence?.peers() ?? []
  }

  /** Открыть заметку на совместную правку */
  const openNote = async (noteId: string, fallbackBody: string): Promise<NoteDoc> => {
    const known = docs.get(noteId)

    if (known) return known

    const doc = new NoteDoc(noteId, touch)

    docs.set(noteId, doc)
    await doc.open(fallbackBody)
    touch()

    return doc
  }

  /** Закрыть заметку: закрепить состояние и отпустить документ */
  const closeNote = async (noteId: string): Promise<void> => {
    const doc = docs.get(noteId)

    if (!doc) return

    docs.delete(noteId)
    await doc.persist()
    doc.destroy()
    touch()
  }

  /** Сказать соседям, где мы и что делаем */
  const setPresence = (state: {
    noteId?: string
    from?: number
    to?: number
    typing?: boolean
  }) => {
    presence?.setLocal(state)
  }

  /**
   * Встреча по идентификатору — из уже загруженного списка, а если её там нет,
   * то с сервера.
   *
   * Второе бывает у ссылки из уведомления: человек открывает встречу до того,
   * как список успел приехать, и отправлять его «куда-то в список» вместо
   * позвавшего разговора значит потерять то, ради чего он кликнул.
   */
  const meetingById = async (id: string): Promise<MeetingSession | null> => {
    const known = meetings.value.find((meeting) => meeting.id === id)

    if (known) return known

    try {
      const meeting = await getMeeting(id)

      // Список мог приехать, пока мы её спрашивали: экран открывают и
      // маршрутом, и переходом из баннера, и оба пути ведут сюда.
      meetings.value = [meeting, ...meetings.value.filter((item) => item.id !== meeting.id)]
      error.value = null

      return meeting
    } catch (failure) {
      error.value = failure

      return null
    }
  }

  /**
   * Положить встречу в список и отдать её экрану.
   *
   * Одним местом для всех действий планирования: и «Запланировать», и перенос,
   * и повтор создания комнаты возвращают одну и ту же строку, и раскладывать
   * её по спискам в каждом обработчике значит забыть один.
   */
  const remember = (meeting: MeetingSession): MeetingSession => {
    meetings.value = [meeting, ...meetings.value.filter((item) => item.id !== meeting.id)]

    if (session.value?.id === meeting.id) {
      session.value = meeting
    }

    return meeting
  }

  /**
   * Запланировать встречу. Ошибку не глотаем: без ящика в домене Google
   * откажет, и экран показывает это своим окном (макет 12-9А).
   */
  const schedule = async (input: ScheduleMeetingInput): Promise<MeetingSession> => {
    const meeting = await scheduleMeeting(input)

    // Начатая «сейчас» встреча — это та, в которой человек сидит: состояние
    // шапки обязано узнать о ней сразу, не дожидаясь опроса.
    if (input.startNow) {
      await refreshState()
    }

    const known = remember(meeting)

    // Новая встреча меняет не только свою строку: серия, продолженная через
    // «Запланировать снова», уезжает из «Прошедших» в «Серии», а созвон с
    // повтором встаёт строкой серии. Страница сама список не перечитывает —
    // до следующего уведомления он показывал новую встречу одиночной.
    await loadMeetings()

    return known
  }

  /** Прикрепить комнату Meet к идущей ад-хок встрече */
  const attachRoom = async (
    sessionId: string,
    title: string,
    recordingEnabled: boolean,
  ): Promise<MeetingSession> => remember(await attachMeet(sessionId, title, recordingEnabled))

  /**
   * Изменить встречу: время, название, повтор; у серии — «только эта» или
   * «эта и следующие».
   *
   * Список перечитываем целиком: одиночная, ставшая серией, переезжает в
   * «Серии», а разрезанная серия меняет не одну свою строку (DEV-2200).
   */
  const reschedule = async (
    sessionId: string,
    input: RescheduleMeetingInput,
  ): Promise<MeetingSession> => {
    const session = remember(await rescheduleMeeting(sessionId, input))

    await loadMeetings()

    return session
  }

  /** Отменить встречу: строка остаётся в списке — заметки живут */
  const cancel = async (sessionId: string, reason: string, scope?: MeetingScope): Promise<void> => {
    await cancelMeeting(sessionId, reason, scope)
    await loadMeetings()
  }

  /**
   * Вернуть отменённый созвон на место.
   *
   * Список перечитываем целиком, как и после отмены: вернувшийся созвон меняет
   * не только свою строку — живая серия уезжает из «Прошедших» обратно в свою
   * группу, а её лицом становится он.
   */
  const restore = async (sessionId: string): Promise<MeetingSession> => {
    const session = remember(await restoreMeeting(sessionId))

    await loadMeetings()

    return session
  }

  /** Позвать на встречу ещё раз — всех, кто уже позван, или только названных */
  const resendInvites = async (sessionId: string, userIds?: string[]): Promise<MeetingSession> =>
    remember(await resendMeetingInvites(sessionId, userIds))

  /** Завести комнату заново после отказа Google */
  const retryRoom = async (sessionId: string): Promise<MeetingSession> =>
    remember(await retryMeetingRoom(sessionId))

  /** Прогнать запись через разбор заново после сбоя обработки */
  const retryProcessing = async (sessionId: string): Promise<MeetingSession> =>
    remember(await retryMeetingProcessing(sessionId))

  /**
   * Убрать человека из состава. Ошибку не глотаем: бэкенд объясняет отказ
   * словами, и экран показывает его текст.
   */
  const removeFrom = async (sessionId: string, userId: string): Promise<MeetingSession> =>
    remember(await removeFromMeeting(sessionId, userId))

  /** Позвать людей в названную встречу — со страницы самой встречи */
  const inviteTo = async (sessionId: string, userIds: string[]): Promise<MeetingSession | null> => {
    const state = await inviteToSession(sessionId, userIds)

    return state.session ? remember(state.session) : null
  }

  const refreshState = async () => {
    if (isForbidden.value) return

    try {
      const state = await getMeetingState()

      session.value = state.session
      invites.value = state.invites
      error.value = null
    } catch (failure) {
      isForbidden.value = forbiddenResponse(failure)
      error.value = failure
    }
  }

  const invite = async (userIds: string[]) => {
    if (!userIds.length) return

    try {
      const state = await inviteToMeeting(userIds)

      session.value = state.session
      invites.value = state.invites
      error.value = null
    } catch (failure) {
      error.value = failure
    }
  }

  const join = async (sessionId: string) => {
    try {
      const state = await joinMeeting(sessionId)

      session.value = state.session
      invites.value = state.invites
      pendingOpen.value = true
      error.value = null
    } catch (failure) {
      error.value = failure
    }
  }

  /** Завершить встречу. Всегда ту, что открыта сейчас, — её id, а не «текущую» */
  const end = async () => {
    const current = session.value

    if (!current) return

    try {
      await endMeeting(current.id)
      // Перечитываем, а не гасим руками: у человека могла остаться другая
      // встреча, и обнулять состояние вслепую значит спрятать её от него.
      await refreshState()
      error.value = null
    } catch (failure) {
      error.value = failure
    }
  }

  /**
   * Держит поток событий встречи, переподключаясь после обрывов.
   *
   * После каждого переподключения лента и участники перечитываются целиком:
   * события, прошедшие мимо во время обрыва, не восстанавливаются, и без
   * догона экран остался бы со вчерашним протоколом, выглядя живым.
   */
  const openStream = (sessionId: string, live = true) => {
    closeStream()

    const notes = useNotesStore()
    const own = new AbortController()

    controller = own
    isOffline.value = false

    // Присутствие — только у идущей встречи: каретки и «печатает заметку…»
    // сервер принимает, пока в неё можно писать, и на разобранной каждая такая
    // отправка была бы отказом (DEV-2142).
    if (live) {
      presence = new MeetingPresence(
        sessionId,
        { userId: auth.user?.id ?? '', name: auth.user?.name ?? '' },
        touch,
      )
      touch()
    }

    void (async () => {
      let attempt = 0

      while (!own.signal.aborted) {
        try {
          await streamMeeting(sessionId, {
            signal: own.signal,
            onEvent: (event) => {
              if (event.kind === 'note') void notes.load()

              if (event.kind === 'participants' || event.kind === 'ended') {
                void refreshState()
                // Экран открытой встречи перечитывает по этому счётчику свою
                // карточку: статус и состав меняются и после конца созвона —
                // разбор записи доезжает часами позже (DEV-2142).
                meetingRevision.value += 1
              }

              // Свои правки применены на месте: возвращать их себе же значит
              // гонять текст по кругу.
              if (event.actorId && event.actorId === auth.user?.id) return

              if (event.kind === 'crdt' && event.noteId && event.payload) {
                docs.get(event.noteId)?.applyRemote(event.payload, event.seq ?? 0)
              }

              // Правка не поместилась в потолок NOTIFY — тела у события нет,
              // и клиент дочитывает журнал сам.
              if (event.kind === 'crdt-gap' && event.noteId) {
                void docs.get(event.noteId)?.catchUp()
              }

              if (event.kind === 'awareness' && event.payload) {
                presence?.applyRemote(event.payload)
              }
            },
          })

          // Поток закончился без ошибки — так бывает при завершении встречи.
          attempt = 0
          isOffline.value = false
        } catch (failure) {
          // Отмена — это наш собственный уход, а не потеря связи: зажигать по
          // ней красную надпись значит пугать человека своим же действием.
          if (own.signal.aborted) return

          // Встречу разобрали — событий больше не будет. Дальше повторять
          // значит стучаться в закрытую дверь каждые пять секунд.
          if (failure instanceof MeetingStreamClosed) return

          attempt += 1
          isOffline.value = true

          // Пока связи нет, чужие каретки не двигаются: оставить их значит
          // показывать, что сосед замер на середине слова, — а это мы его не
          // слышим.
          presence?.forgetPeers()
        }

        if (own.signal.aborted) return

        const delay = RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)] ?? 5_000

        await new Promise((resolve) => setTimeout(resolve, delay))

        if (own.signal.aborted) return

        // Догоняем пропущенное до того, как снова слушать.
        await refreshState()

        // Идущей встречи может и не быть: смотрят разбор той, что кончилась.
        // Цикл останавливает не она, а сам сервер — отказом по разобранной
        // (DEV-2142). Раньше здесь стоял выход по её отсутствию, и первый же
        // обрыв гасил поток навсегда именно у тех, кому он теперь и нужен.
        void notes.load()

        // Правки, прошедшие мимо во время обрыва, догоняются журналом: у
        // каждой заметки свои номера, и по разрыву в них видно пропущенное.
        for (const doc of docs.values()) void doc.catchUp()

        isOffline.value = false
      }
    })()
  }

  const closeStream = () => {
    controller?.abort()
    controller = null
    isOffline.value = false

    for (const noteId of [...docs.keys()]) void closeNote(noteId)

    presence?.destroy()
    presence = null
    touch()
  }

  const consumePendingOpen = () => {
    const wanted = pendingOpen.value
    pendingOpen.value = false

    return wanted
  }

  return {
    session,
    invites,
    meetings,
    isListLoading,
    isListLoaded,
    loadMeetings,
    meetingById,
    schedule,
    attachRoom,
    reschedule,
    cancel,
    restore,
    resendInvites,
    retryRoom,
    retryProcessing,
    inviteTo,
    removeFrom,
    revision,
    peers,
    openNote,
    closeNote,
    setPresence,
    error,
    isForbidden,
    isActive,
    isAuthor,
    isOffline,
    meetingRevision,
    participants,
    everyone,
    pendingOpen,
    refreshState,
    invite,
    join,
    end,
    openStream,
    closeStream,
    consumePendingOpen,
  }
})
